/**
 * Outil « Bataille navale du repère » — calculs (pur).
 *
 * Points à coordonnées entières dans un repère : de 0 à 8 en 6e (premier
 * quart), de −5 à 5 dès la 5e (nombres relatifs). On reconnaît les erreurs
 * typiques d'un point mal placé : abscisse et ordonnée inversées, signe de
 * l'une ou de l'autre. La flotte : trois bateaux (3, 2, 2 cases) alignés sur
 * les points du quadrillage, sans se toucher.
 */

import { seededRng } from '../../ui/random';

export interface Pt {
  x: number;
  y: number;
}
export interface Bounds {
  min: number;
  max: number;
}
export interface Ship {
  cells: Pt[];
}

export function boundsFor(rank: number | null): Bounds {
  return rank !== null && rank <= 1 ? { min: 0, max: 8 } : { min: -5, max: 5 };
}

const same = (a: Pt, b: Pt) => a.x === b.x && a.y === b.y;
const randInt = (rng: () => number, lo: number, hi: number) =>
  lo + Math.floor(rng() * (hi - lo + 1));

/** « (3 ; −2) » */
export function coordText(p: Pt): string {
  const n = (v: number) => String(v).replace('-', '−');
  return `(${n(p.x)} ; ${n(p.y)})`;
}

/** Un point au hasard, différent des précédents et (en relatifs) pas sur un axe une fois sur deux. */
export function randomPoint(rng: () => number, b: Bounds, avoid: readonly Pt[] = []): Pt {
  for (let tries = 0; tries < 200; tries++) {
    const p = { x: randInt(rng, b.min, b.max), y: randInt(rng, b.min, b.max) };
    if (avoid.some((q) => same(p, q))) continue;
    // assez de points « intéressants » : x ≠ y, et hors des axes le plus souvent
    if (p.x === p.y && tries < 100) continue;
    if ((p.x === 0 || p.y === 0) && rng() < 0.7) continue;
    return p;
  }
  return { x: b.max, y: b.min === 0 ? 1 : b.min };
}

export type Feedback = 'ok' | 'swapped' | 'signX' | 'signY' | 'signs' | 'wrong';

/** Que s'est-il passé quand on a placé (ou lu) `placed` au lieu de `target` ? */
export function feedback(target: Pt, placed: Pt): Feedback {
  if (same(target, placed)) return 'ok';
  if (target.x !== target.y && placed.x === target.y && placed.y === target.x) return 'swapped';
  if (target.x !== 0 && target.y !== 0 && placed.x === -target.x && placed.y === -target.y)
    return 'signs';
  if (target.x !== 0 && placed.x === -target.x && placed.y === target.y) return 'signX';
  if (target.y !== 0 && placed.x === target.x && placed.y === -target.y) return 'signY';
  return 'wrong';
}

/** Trois bateaux (3, 2, 2 cases), horizontaux ou verticaux, sans se toucher (même en coin). */
export function placeFleet(
  rng: () => number,
  b: Bounds,
  sizes: readonly number[] = [3, 2, 2],
): Ship[] {
  for (let attempt = 0; attempt < 500; attempt++) {
    const ships: Ship[] = [];
    let ok = true;
    for (const size of sizes) {
      let placed = false;
      for (let t = 0; t < 100 && !placed; t++) {
        const horizontal = rng() < 0.5;
        const x0 = randInt(rng, b.min, b.max - (horizontal ? size - 1 : 0));
        const y0 = randInt(rng, b.min, b.max - (horizontal ? 0 : size - 1));
        const cells = Array.from({ length: size }, (_, i) =>
          horizontal ? { x: x0 + i, y: y0 } : { x: x0, y: y0 + i },
        );
        const touches = ships.some((s) =>
          s.cells.some((c) =>
            cells.some((d) => Math.abs(c.x - d.x) <= 1 && Math.abs(c.y - d.y) <= 1),
          ),
        );
        if (!touches) {
          ships.push({ cells });
          placed = true;
        }
      }
      if (!placed) {
        ok = false;
        break;
      }
    }
    if (ok) return ships;
  }
  throw new Error('flotte impossible à placer');
}

export type Shot = 'miss' | 'hit' | 'sunk' | 'repeat';

/** Résultat d'un tir en `p`, compte tenu des tirs déjà faits (`before`). */
export function fire(fleet: readonly Ship[], before: readonly Pt[], p: Pt): Shot {
  if (before.some((q) => same(q, p))) return 'repeat';
  const ship = fleet.find((s) => s.cells.some((c) => same(c, p)));
  if (!ship) return 'miss';
  const all = [...before, p];
  return ship.cells.every((c) => all.some((q) => same(q, c))) ? 'sunk' : 'hit';
}

export function isHit(fleet: readonly Ship[], p: Pt): boolean {
  return fleet.some((s) => s.cells.some((c) => same(c, p)));
}

export function sunkShips(fleet: readonly Ship[], shots: readonly Pt[]): Ship[] {
  return fleet.filter((s) => s.cells.every((c) => shots.some((q) => same(q, c))));
}

export function newGame(seed: number, b: Bounds): Ship[] {
  return placeFleet(seededRng(seed), b);
}
